import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import ListItemText from '@mui/material/ListItemText';
import CompletionLineChart from '../CompletionLineChart/CompletionLineChart';
import CompletionByUnitBarChart from '../CompletionByUnitBarChart/CompletionByUnitBarChart';
import { fetchCompletionHistory } from '../../api/stackersApi';
import './StackerProjectCard.scss';

// Circular completion gauge: arc shows percent, value and labels are plain text
function CompletionDonut({ percent, label, unitCount, size = 68 }) {
  const pct = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = (pct / 100) * c;
  const center = size / 2;
  return (
    <div
      className="completion-donut"
      role="img"
      aria-label={`${label || 'Completion'}: ${pct}% complete${unitCount != null ? ` across ${unitCount} stackers` : ''}`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={center} cy={center} r={r} fill="none" stroke="#e9ecef" strokeWidth={stroke} />
        {pct > 0 && (
          <circle
            cx={center}
            cy={center}
            r={r}
            fill="none"
            stroke="#198754"
            strokeWidth={stroke}
            strokeDasharray={`${filled} ${Math.max(0, c - filled)}`}
            strokeLinecap={pct < 100 ? 'round' : 'butt'}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          className="completion-donut__value"
        >
          {pct}%
        </text>
      </svg>
      {label && <div className="completion-donut__label">{label}</div>}
      {unitCount != null && <div className="completion-donut__sublabel">{unitCount} stacker{unitCount === 1 ? '' : 's'}</div>}
    </div>
  );
}

function StackerProjectCard({ project }) {
  const navigate = useNavigate();
  const [showLineModal, setShowLineModal] = useState(false);
  const [showBarModal, setShowBarModal] = useState(false);
  // Use completion history from summary when available so chart shows immediately
  const [history, setHistory] = useState(() => (Array.isArray(project?.completionHistory) ? project.completionHistory : []));
  const hasImage = project.thumbnailUrl && project.thumbnailUrl.trim();
  const completion = project.completionPercent ?? 0;

  useEffect(() => {
    if (Array.isArray(project?.completionHistory)) return;
    const token = sessionStorage.getItem('token');
    if (!project?.name) return;
    fetchCompletionHistory(project.name, token)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [project?.name, project?.completionHistory]);

  // Sync when project (e.g. from summary) gets completionHistory after initial mount
  useEffect(() => {
    if (Array.isArray(project?.completionHistory)) setHistory(project.completionHistory);
  }, [project?.completionHistory]);

  const handleCardClick = (e) => {
    if (e.target.closest('.stacker-chart-clickable')) return;
    navigate(`/stacker-project/${encodeURIComponent(project.name)}`);
  };

  const handleCardKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (e.target.closest('.stacker-chart-clickable')) return;
    navigate(`/stacker-project/${encodeURIComponent(project.name)}`);
  };

  const chartId = `stacker-line-${(project.name || '').replace(/\W/g, '-')}`;
  const barChartId = `stacker-bar-${(project.name || '').replace(/\W/g, '-')}`;
  const labels = (history || []).map((h) => h.report_date || '');
  const data = (history || []).map((h) => (h.completion_percent != null ? Number(h.completion_percent) : 0));
  const hasChartData = labels.length > 0 && data.length > 0;

  const completionByUnit = Array.isArray(project?.completionByUnit) ? project.completionByUnit : [];
  const unitLabels = completionByUnit.map((u) => u.unit_name || '');
  const unitData = completionByUnit.map((u) => (u.completion_percent != null ? Number(u.completion_percent) : 0));
  const hasBarData = unitLabels.length > 0 && unitData.length > 0;

  const options = { timeZone: 'America/New_York', hour12: true };
  const time = new Date().toLocaleString('en-US', options);

  // One gauge per stacker type; fall back to a single overall gauge when the
  // server doesn't send completionByType yet
  const completionByType = Array.isArray(project?.completionByType) && project.completionByType.length
    ? project.completionByType
    : [{ stackerType: project.stackerType, totalUnits: project.totalUnits, completionPercent: completion }];

  return (
    <>
      <div className="stacker-card-full full__container" onClick={handleCardClick} role="button" tabIndex={0} onKeyDown={handleCardKeyDown}>
        <div className="top__container">
          <div className="left__container stacker-card-left">
            <h5 className="m-0">{time}</h5>
            <div className="stacker-card__image-wrap card__image-wrap">
              {hasImage ? (
                <img className="card__image stacker-card__image" src={project.thumbnailUrl} alt={project.name} />
              ) : (
                <div className="stacker-card__placeholder">No image</div>
              )}
            </div>
            <div className="table__metrics__revenue__container stacker-completion-badge">
              <p className="table__metrics__revenue__text">Completion: {completion}%</p>
            </div>
            <div className="ticket__info__container">
              <div>
                <ListItemText primary="Stackers" secondary={project.totalUnits ?? '—'} />
                <ListItemText primary="Stacker type" secondary={project.stackerType || '—'} />
                <ListItemText primary="Posts" secondary={project.posts != null ? project.posts : '—'} />
                <ListItemText primary="Reports" secondary={project.reportCount ?? 0} />
              </div>
              <div className="stacker-type-donuts">
                {completionByType.map((t, i) => (
                  <CompletionDonut
                    key={t.stackerType || i}
                    percent={t.completionPercent}
                    label={completionByType.length > 1 || t.stackerType ? (t.stackerType || 'Unknown type') : null}
                    unitCount={t.totalUnits}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="right__container right__container__upper stacker-charts-wrap">
            <div
              className="stacker-chart-cell stacker-chart-clickable"
              onClick={(e) => {
                e.stopPropagation();
                if (hasChartData) setShowLineModal(true);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && hasChartData) {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLineModal(true);
                }
              }}
              aria-label="View completion progress (opens larger chart)"
            >
              {hasChartData ? (
                <CompletionLineChart
                  chartId={chartId}
                  title="Completion progress"
                  labels={labels}
                  data={data}
                  height={200}
                />
              ) : (
                <div className="stacker-completion-placeholder" style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                  No completion history yet
                </div>
              )}
            </div>
            <div
              className="stacker-chart-cell stacker-chart-clickable"
              onClick={(e) => {
                e.stopPropagation();
                if (hasBarData) setShowBarModal(true);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && hasBarData) {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowBarModal(true);
                }
              }}
              aria-label="View completion by unit (opens larger chart)"
            >
              {hasBarData ? (
                <CompletionByUnitBarChart
                  chartId={barChartId}
                  title="Completion by unit"
                  labels={unitLabels}
                  data={unitData}
                  height={200}
                />
              ) : (
                <div className="stacker-completion-placeholder" style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                  No unit data yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showLineModal}
        onHide={() => setShowLineModal(false)}
        size="lg"
        centered
        className="stacker-line-chart-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Completion progress — {project.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showLineModal && hasChartData && (
            <>
              <CompletionLineChart
                chartId={`${chartId}-modal`}
                title="Completion progress"
                labels={labels}
                data={data}
                height={380}
                containerStyle={{ maxWidth: '100%', minHeight: 380 }}
              />
              <div className="stacker-line-modal-table-wrap">
                <table className="table table-sm table-striped mt-3">
                  <thead>
                    <tr>
                      <th>Report date</th>
                      <th className="text-end">Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(history || []).map((h, i) => (
                      <tr key={h.report_date || i}>
                        <td>{h.report_date || '—'}</td>
                        <td className="text-end">{h.completion_percent != null ? Number(h.completion_percent) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showBarModal}
        onHide={() => setShowBarModal(false)}
        size="lg"
        centered
        className="stacker-bar-chart-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Completion by unit — {project.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showBarModal && hasBarData && (
            <>
              <CompletionByUnitBarChart
                chartId={`${barChartId}-modal`}
                title="Completion by unit"
                labels={unitLabels}
                data={unitData}
                height={380}
                maxTicksLimit={undefined}
                containerStyle={{ maxWidth: '100%', minHeight: 380 }}
              />
              <div className="stacker-bar-modal-table-wrap">
                <table className="table table-sm table-striped mt-3">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th className="text-end">Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completionByUnit.map((u) => (
                      <tr key={u.unit_name}>
                        <td>{u.unit_name}</td>
                        <td className="text-end">{u.completion_percent != null ? Number(u.completion_percent) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default StackerProjectCard;
